import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const REPORT_STATUS_COLORS = {
  open:        { bg:'#FEF2F2', color:'#DC2626', label:'🔴 Open' },
  in_progress: { bg:'#FEF3C7', color:'#D97706', label:'🟡 In Progress' },
  resolved:    { bg:'#F0FDF4', color:'#16A34A', label:'🟢 Resolved' },
};

export default function SupportPage() {
  const navigate = useNavigate();
  const [tab, setTab]         = useState('crops');
  const [mobile, setMobile]   = useState('');
  const [userData, setUserData] = useState(null);
  const [searching, setSearching] = useState(false);

  // Reports state
  const [reports, setReports]         = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportFilter, setReportFilter]     = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminNote, setAdminNote]           = useState('');
  const [saving, setSaving]                 = useState(false);

  // FAQ state
  const [faqs, setFaqs]           = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(false);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [faqForm, setFaqForm]     = useState({ question:'', answer:'', order:0 });
  const [faqSaving, setFaqSaving] = useState(false);

  useEffect(() => {
    if (tab === 'reports') loadReports();
    if (tab === 'faqs')    loadFaqs();
  }, [tab, reportFilter]);

  // ── Existing functions ────────────────────────────────────
  const findUser = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) return toast.error('Enter valid mobile number');
    setSearching(true);
    try {
      const { data } = await api.get('/admin/users?search=' + mobile + '&limit=1');
      if (data.users?.length) setUserData(data.users[0]);
      else toast.error('User not found');
    } catch { toast.error('Error'); }
    finally { setSearching(false); }
  };

  const closeCrop = async (cropId, cropName) => {
    if (!window.confirm('Close crop ' + cropName + ' on behalf of farmer?')) return;
    try {
      await api.put('/admin/crops/' + cropId + '/status', { status:'closed', adminNote:'Closed by admin via customer support' });
      toast.success('Crop closed successfully');
    } catch { toast.error('Error closing crop'); }
  };

  // ── Reports functions ────────────────────────────────────
  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const q = reportFilter !== 'all' ? `?status=${reportFilter}` : '';
      const { data } = await api.get(`/support/admin/reports${q}`);
      setReports(data.reports || []);
    } catch { toast.error('Failed to load reports'); }
    finally { setReportsLoading(false); }
  };

  const updateReport = async (id, status) => {
    setSaving(true);
    try {
      await api.put(`/support/admin/reports/${id}`, { status, adminNote });
      toast.success('Report updated ✅');
      setSelectedReport(null);
      loadReports();
    } catch { toast.error('Error updating report'); }
    finally { setSaving(false); }
  };

  // ── FAQ functions ─────────────────────────────────────────
  const loadFaqs = async () => {
    setFaqsLoading(true);
    try {
      const { data } = await api.get('/support/admin/faqs');
      setFaqs(data.faqs || []);
    } catch { toast.error('Failed to load FAQs'); }
    finally { setFaqsLoading(false); }
  };

  const saveFaq = async () => {
    if (!faqForm.question || !faqForm.answer) return toast.error('Question and answer required');
    setFaqSaving(true);
    try {
      if (faqForm._id) {
        await api.put(`/support/admin/faqs/${faqForm._id}`, faqForm);
        toast.success('FAQ updated ✅');
      } else {
        await api.post('/support/admin/faqs', faqForm);
        toast.success('FAQ added ✅');
      }
      setShowFaqForm(false);
      setFaqForm({ question:'', answer:'', order:0 });
      loadFaqs();
    } catch { toast.error('Error saving FAQ'); }
    finally { setFaqSaving(false); }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await api.delete(`/support/admin/faqs/${id}`);
      toast.success('FAQ deleted');
      loadFaqs();
    } catch { toast.error('Error'); }
  };

  const toggleFaq = async (faq) => {
    try {
      await api.put(`/support/admin/faqs/${faq._id}`, { ...faq, isActive: !faq.isActive });
      loadFaqs();
    } catch { toast.error('Error'); }
  };

  return (
    <AdminLayout title="Customer Support">
      <div className="tabs">
        <button className={'tab' + (tab==='crops'  ? ' tab--active':'')} onClick={() => setTab('crops')}> Fix Crops</button>
        <button className={'tab' + (tab==='users'  ? ' tab--active':'')} onClick={() => setTab('users')}>👤 User Lookup</button>
        <button className={'tab' + (tab==='reports'? ' tab--active':'')} onClick={() => setTab('reports')}>🐛 Reports</button>
        <button className={'tab' + (tab==='faqs'   ? ' tab--active':'')} onClick={() => setTab('faqs')}>❓ FAQs</button>
        <button className={'tab' + (tab==='guide'  ? ' tab--active':'')} onClick={() => setTab('guide')}>📋 Guide</button>
      </div>

      {/* ── Fix Crops tab (existing) ── */}
      {tab === 'crops' && (
        <>
          <div className="alert alert--primary" style={{ marginBottom:16 }}>
            💡 Use this when a farmer contacts support saying they cannot close their crop.
          </div>
          <div className="card">
            <div className="card__header"><div className="card__title">🔍 Find Farmer by Mobile</div></div>
            <div className="card__body">
              <div style={{ display:'flex', gap:10, marginBottom:16 }}>
                <input className="input" type="tel" placeholder="Farmer mobile number" maxLength={10}
                  value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,''))} style={{ flex:1 }} />
                <button className="btn btn--primary" onClick={findUser} disabled={searching}>
                  {searching ? <span className="spinner spinner--sm spinner--white" /> : '🔍 Find'}
                </button>
              </div>
              {userData && (
               <div>
                  <div style={{ background:'#f8f9fa', borderRadius:8, padding:'12px 14px', marginBottom:14 }}>
                    <div style={{ fontWeight:600 }}>{userData.name}</div>
                    <div style={{ fontSize:12, color:'#6C757D' }}>📱 +91 {userData.mobile} ·  {userData.locationAddress||'—'}</div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Crops:</div>
                  <FarmerCrops userId={userData._id} onClose={closeCrop} />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── User Lookup tab (existing) ── */}
      {tab === 'users' && (
        <div className="card">
          <div className="card__header"><div className="card__title">👤 User Account Lookup</div></div>
          <div className="card__body">
            <div style={{ display:'flex', gap:10, marginBottom:16 }}>
              <input className="input" type="tel" placeholder="User mobile number" maxLength={10}
                value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,''))} style={{ flex:1 }} />
              <button className="btn btn--primary" onClick={findUser} disabled={searching}>
                {searching ? <span className="spinner spinner--sm spinner--white" /> : '🔍 Find'}
              </button>
            </div>
            {userData && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {[
                  ['Name', userData.name||'—'], ['Mobile', '+91 '+userData.mobile],
                  ['Role', userData.role], ['Location', userData.locationAddress||'—'],
                  ['Status', userData.isActive ? 'Active':'Banned'], ['Joined', new Date(userData.createdAt).toLocaleDateString('en-IN')],
                  ['Referral Code', userData.referralCode||'—'], ['Crops', userData.cropCount||0],
                ].map(([k,v]) => (
                  <div key={k} style={{ background:'#f8f9fa', borderRadius:6, padding:'10px 12px' }}>
                    <div style={{ fontSize:11, color:'#6C757D' }}>{k}</div>
                    <div style={{ fontSize:13, fontWeight:600, marginTop:2 }}>{String(v)}</div>
                  </div>
                ))}
                <div style={{ gridColumn:'1/-1', marginTop:8 }}>
                  <button className="btn btn--outline" onClick={() => navigate('/users')}>View in Users</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Reports tab (new) ── */}
      {tab === 'reports' && (
        <div>
          {/* Filter */}
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
            {['all','open','in_progress','resolved'].map(s => (
              <button key={s} onClick={() => setReportFilter(s)}
                style={{ padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
                  background: reportFilter===s ? '#1A6B3C' : '#F1F5F9',
                  color: reportFilter===s ? 'white' : '#64748B' }}>
                {s === 'all' ? 'All' : REPORT_STATUS_COLORS[s]?.label}
              </button>
            ))}
          </div>

          {reportsLoading ? <div className="page-loader"><span className="spinner spinner--sm" /></div> : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {reports.map(r => (
                <div key={r._id} className="card" style={{ padding:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ background:REPORT_STATUS_COLORS[r.status]?.bg, color:REPORT_STATUS_COLORS[r.status]?.color, borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:600 }}>
                        {REPORT_STATUS_COLORS[r.status]?.label}
                      </span>
                      <span style={{ fontSize:12, color:'#94A3B8' }}>
                        {new Date(r.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                      </span>
                    </div>
                    <span style={{ fontSize:12, background:'#F1F5F9', padding:'2px 8px', borderRadius:8, color:'#64748B' }}>{r.category}</span>
                  </div>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>
                    {r.name || 'Unknown'} · +91 {r.mobile}
                  </div>
                  <div style={{ fontSize:13, color:'#475569', lineHeight:1.5, marginBottom:10 }}>{r.message}</div>
                  {r.adminNote && (
                    <div style={{ background:'#F0FDF4', borderRadius:8, padding:'8px 10px', fontSize:12, color:'#1A6B3C', marginBottom:10 }}>
                      📝 {r.adminNote}
                    </div>
                  )}
                  <button onClick={() => { setSelectedReport(r); setAdminNote(r.adminNote || ''); }}
                    style={{ padding:'6px 14px', background:'#1A6B3C', color:'white', border:'none', borderRadius:8, fontSize:13, cursor:'pointer', fontWeight:600 }}>
                    Update Status
                  </button>
                </div>
              ))}
              {reports.length === 0 && <div style={{ textAlign:'center', color:'#94A3B8', padding:40 }}>No reports found</div>}
            </div>
          )}
        </div>
      )}

      {/* ── FAQs tab (new) ── */}
      {tab === 'faqs' && (
        <div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
            <button onClick={() => { setFaqForm({ question:'', answer:'', order:0 }); setShowFaqForm(true); }}
              className="btn btn--primary">
              + Add FAQ
            </button>
          </div>
          {faqsLoading ? <div className="page-loader"><span className="spinner spinner--sm" /></div> : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {faqs.map(faq => (
                <div key={faq._id} className="card" style={{ padding:16, opacity: faq.isActive ? 1 : 0.5 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>Q: {faq.question}</div>
                      <div style={{ fontSize:13, color:'#475569', lineHeight:1.5 }}>A: {faq.answer}</div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <button onClick={() => toggleFaq(faq)}
                        style={{ padding:'4px 10px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
                          background: faq.isActive ? '#F0FDF4' : '#FEF2F2',
                          color: faq.isActive ? '#16A34A' : '#DC2626' }}>
                        {faq.isActive ? 'Active' : 'Hidden'}
                      </button>
                      <button onClick={() => { setFaqForm(faq); setShowFaqForm(true); }}
                        className="btn btn--outline btn--sm">Edit</button>
                      <button onClick={() => deleteFaq(faq._id)}
                        className="btn btn--danger btn--sm">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
              {faqs.length === 0 && <div style={{ textAlign:'center', color:'#94A3B8', padding:40 }}>No FAQs yet. Add one!</div>}
            </div>
          )}
        </div>
      )}

      {/* ── Guide tab (existing) ── */}
      {tab === 'guide' && (
        <div className="card">
          <div className="card__body">
            <div style={{ lineHeight:2, fontSize:13 }}>
              <h3 style={{ marginBottom:12 }}>📋 Common Support Issues & Solutions</h3>
              {[
                { issue:'Farmer cannot close crop', fix:'Go to Fix Crops tab → search mobile → close crop on their behalf' },
                { issue:'User forgot their mobile / lost phone', fix:'They must use their registered number. Create a new account if needed.' },
                { issue:'Wrong location on profile', fix:'Ask user to update profile → Edit Profile → Location field' },
                { issue:'OTP not received', fix:'Check SMS provider balance. Use DEFAULT_OTP = 111111 in testing mode.' },
                { issue:'Buyer cannot see crop', fix:'Crop must be status: ready_to_sell to appear in marketplace' },
                { issue:'Request not matched to farmer', fix:'System matches by crop name + location. Farmer must have same crop name.' },
                { issue:'User wants account deleted', fix:'Go to Users page → find user → Ban user (we retain data for audit)' },
              ].map((item,i) => (
                <div key={i} style={{ background:'#f8f9fa', borderRadius:8, padding:'12px 14px', marginBottom:10 }}>
                  <div style={{ fontWeight:600, color:'#DC2626', marginBottom:4 }}>❓ {item.issue}</div>
                  <div style={{ color:'#16A34A' }}>✅ {item.fix}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Update report modal ── */}
      {selectedReport && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'white', borderRadius:16, padding:24, width:'100%', maxWidth:480 }}>
            <h3 style={{ marginBottom:8 }}>Update Report</h3>
            <div style={{ fontSize:12, color:'#94A3B8', marginBottom:12 }}>{selectedReport.name} · +91 {selectedReport.mobile}</div>
            <div style={{ fontSize:13, color:'#475569', marginBottom:16, background:'#F8FAFC', padding:12, borderRadius:8, lineHeight:1.5 }}>
              {selectedReport.message}
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:8 }}>Change Status:</label>
              <div style={{ display:'flex', gap:8 }}>
                {['open','in_progress','resolved'].map(s => (
                  <button key={s} onClick={() => updateReport(selectedReport._id, s)} disabled={saving}
                    style={{ flex:1, padding:'8px', borderRadius:8, border:`2px solid ${REPORT_STATUS_COLORS[s]?.color}`,
                      background: REPORT_STATUS_COLORS[s]?.bg, color: REPORT_STATUS_COLORS[s]?.color,
                      cursor:'pointer', fontSize:12, fontWeight:600 }}>
                    {saving ? '...' : REPORT_STATUS_COLORS[s]?.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:6 }}>Admin Note</label>
              <textarea style={{ width:'100%', padding:10, borderRadius:8, border:'1px solid #E2E8F0', fontSize:13, resize:'vertical' }}
                rows={3} placeholder="Internal note..." value={adminNote} onChange={e => setAdminNote(e.target.value)} />
            </div>
            <button onClick={() => setSelectedReport(null)} className="btn btn--outline" style={{ width:'100%' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── FAQ form modal ── */}
      {showFaqForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'white', borderRadius:16, padding:24, width:'100%', maxWidth:480 }}>
            <h3 style={{ marginBottom:16 }}>{faqForm._id ? 'Edit FAQ' : 'Add FAQ'}</h3>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:6 }}>Question *</label>
              <input className="input" placeholder="e.g. How do I add a crop?"
                value={faqForm.question} onChange={e => setFaqForm(p => ({ ...p, question: e.target.value }))} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:6 }}>Answer *</label>
              <textarea className="input" rows={4} placeholder="Detailed answer..."
                value={faqForm.answer} onChange={e => setFaqForm(p => ({ ...p, answer: e.target.value }))} />
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:6 }}>Role *</label>
              <select className="input"
                value={faqForm.role || 'all'}
                onChange={e => setFaqForm(p => ({ ...p, role: e.target.value }))}>
                <option value="all">🌐 All Roles</option>
                <option value="seller"> Farmer / Seller</option>
                <option value="buyer">🛒 Buyer</option>
                <option value="agent">🤝 Agent</option>
              </select>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:6 }}>Order (lower = first)</label>
              <input type="number" className="input"
                value={faqForm.order} onChange={e => setFaqForm(p => ({ ...p, order: Number(e.target.value) }))} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setShowFaqForm(false)} className="btn btn--outline" style={{ flex:1 }}>Cancel</button>
              <button onClick={saveFaq} disabled={faqSaving} className="btn btn--primary" style={{ flex:2 }}>
                {faqSaving ? 'Saving...' : faqForm._id ? 'Update FAQ' : 'Add FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

// ── Existing FarmerCrops component (unchanged) ────────────────
function FarmerCrops({ userId, onClose }) {
  const [crops, setCrops]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/crops?farmer=' + userId + '&limit=20')
      .then(({ data }) => setCrops(data.crops || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="page-loader"><span className="spinner spinner--sm" /></div>;
  if (!crops.length) return <div style={{ color:'#6C757D', fontSize:13 }}>No crops found for this farmer.</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {crops.map(crop => (
        <div key={crop._id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:'#f8f9fa', borderRadius:8, border:'1px solid #e9ecef' }}>
          <div style={{ flex:1 }}>
            <span style={{ fontWeight:600 }}>{crop.cropName}</span>
            <span style={{ fontSize:12, color:'#6C757D', marginLeft:8 }}>{crop.category} · Day {crop.currentDayCount||0}</span>
          </div>
          <span className={'badge ' + (crop.status==='growing'?'badge--success':crop.status==='ready_to_sell'?'badge--info':crop.status==='closed'?'badge--gray':'badge--warning')}>
            {crop.status?.replace('_',' ')}
          </span>
          {crop.status !== 'closed' && (
            <button className="btn btn--danger btn--sm" onClick={() => onClose(crop._id, crop.cropName)}>
              🔒 Close
            </button>
          )}
        </div>
      ))}
    </div>
  );
}